import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../context/SnackbarContext";
import UsersService from "@velocity-ehs/user-utils-module";
import { SnackbarType } from "../components/sharedComponents/Snackbar";
import {
  ActivateInactivateUserInput,
  CreatePersonInput,
  CreateUserFromPersonInput,
  Person,
  UpdatePersonInput,
} from "../components/routeComponents/Persons/Person";
import GatewayService from "../services/GatewayService";
import { getStringDate } from "../components/routeComponents/Persons/HelperFunctions";

export enum PersonActions {
  CREATE = "create",
  UPDATE = "update",
  CREATE_USER_FROM_PERSON = "create_user_from_person",
  ACTIVATE_INACTIVATE_USER = "activate_inactivate_user",
}

const usePersonMutation = () => {
  const create = useMutation(
    (req: CreatePersonInput) => GatewayService.createPerson(req),
    ResponseHandling("person_create_success_message")
  );
  const update = useMutation(
    (req: UpdatePersonInput) => GatewayService.updatePerson(req),
    ResponseHandling("person_update_success_message")
  );

  const createUserFromPerson = useMutation(
    (req: CreateUserFromPersonInput) =>
      GatewayService.createUserFromPerson(req),
    ResponseHandling("person_user_created")
  );

  const activateInactivateUser = useMutation(
    (req: ActivateInactivateUserInput) =>
      GatewayService.activateInactivateUser(req),
    ActivateInactivateResponseHandling()
  );

  const mutate = async (
    action: PersonActions,
    input: Person,
    personId?: string
  ) => {
    switch (action) {
      case PersonActions.CREATE:
        const createPerson = mapFormToCreate(input);
        return await create.mutateAsync(createPerson);
      case PersonActions.UPDATE:
        const updatePerson = mapFormToUpdate(input, personId);
        return await update.mutateAsync(updatePerson);
      case PersonActions.CREATE_USER_FROM_PERSON:
        const createUser = mapFormToCreateUser(input);
        return await createUserFromPerson.mutateAsync(createUser);
      case PersonActions.ACTIVATE_INACTIVATE_USER:
        const updateUser = mapFormToActivateInactivateUser(input);
        return await activateInactivateUser.mutateAsync(updateUser);
      default:
        throw new Error(`Invalid action: ${action}`);
    }
  };

  return {
    mutations: {
      mutate,
      isLoading:
        create.isLoading || update.isLoading || createUserFromPerson.isLoading,
    },
  };
};

export default usePersonMutation;

function mapFormToCreate(input: Person): CreatePersonInput {
  return {
    customerId: UsersService.customer?.id,
    birthDate: getStringDate(input?.birthDate),
    currentPositionStartDate: getStringDate(input?.currentPositionStartDate),
    effectiveDate: getStringDate(input?.effectiveDate),
    endDate: getStringDate(input?.endDate),
    hireDate: getStringDate(input?.hireDate),
    contractingCompany: input?.contractingCompany?.id,
    primaryLocation: input?.primaryLocation?.id,
    relationshipToCustomer: input?.relationshipToCustomer?.id,
    supervisor: input?.supervisor?.id,
    yearsInJob: input?.yearsInJob,
    address2: input?.address2,
    address3: input?.address3,
    address4: input?.address4,
    city: input?.city,
    country: input?.country,
    currentPosition: input?.currentPosition,
    displayName: input?.displayName,
    email: input?.email,
    employeeID: input?.employeeID,
    faxNumber: input?.faxNumber,
    firstName: input?.firstName,
    gender: extractIdFromObject(input?.gender),
    lastName: input?.lastName,
    middleName: input?.middleName,
    phone: input?.phone,
    stateProv: input?.stateProv,
    streetAddress: input?.streetAddress,
    zipPostal: input?.zipPostal,
  };
}

function mapFormToUpdate(input: Person, personId: string) {
  return {
    id: personId,
    address2: input?.address2,
    address3: input?.address3,
    address4: input?.address4,
    birthDate: getStringDate(input?.birthDate),
    city: input?.city,
    contractingCompany: input?.contractingCompany?.id,
    country: input?.country,
    currentPosition: input?.currentPosition,
    currentPositionStartDate: getStringDate(input?.currentPositionStartDate),
    displayName: input?.displayName,
    effectiveDate: getStringDate(input?.effectiveDate),
    email: input?.email,
    employeeID: input?.employeeID,
    endDate: getStringDate(input?.endDate),
    faxNumber: input?.faxNumber,
    firstName: input?.firstName,
    gender: extractIdFromObject(input?.gender),
    hireDate: getStringDate(input?.hireDate),
    lastName: input?.lastName,
    middleName: input?.middleName,
    phone: input?.phone,
    primaryLocation: input?.primaryLocation ? input?.primaryLocation?.id : null,
    relationshipToCustomer: input.relationshipToCustomer?.id,
    stateProv: input?.stateProv,
    streetAddress: input?.streetAddress,
    supervisor: input?.supervisor?.id,
    yearsInJob: input?.yearsInJob,
    zipPostal: input?.zipPostal,
  };
}

function mapFormToCreateUser(input: Person): CreateUserFromPersonInput {
  return {
    customerId: input.customerId,
    personId: input.id,
    username: input.username,
    email: input.email,
    firstName: input.firstName,
    lastName: input.lastName,
  };
}

function mapFormToActivateInactivateUser(
  input: Person
): ActivateInactivateUserInput {
  return {
    active: input.active,
    id: input.userId,
    username: input.username,
  };
}

function ResponseHandling(
  messageKey: string = "person_create_success_message"
) {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: () => {
      showSnackbar(SnackbarType.Success, t(messageKey));
    },
  };
}

function ActivateInactivateResponseHandling() {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  return {
    onError: (error) => {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    },
    onSuccess: (response: { active: boolean }) => {
      showSnackbar(
        SnackbarType.Success,
        response.active
          ? t("person_user_activated")
          : t("person_user_inactivated")
      );
    },
  };
}

const extractIdFromObject = (input: any): string | undefined =>
  input?.id ?? undefined;
